import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import styles from "./EstimateLayoutStep2.module.scss";
import classNames from "classnames/bind";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import FormInpuEditor from "~/components/common/form/formInpuEditor/FormInpuEditor";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import type { EstimateLayoutStep2FormData as Step2FormData } from "~/utils/interface/estimateSetting";
import { IEstimateTemplate } from "~/utils/interface/estimateSetting";
import { useState, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetEstimateTemplates } from "~/thunks/estimate-setting/estimateSettingThunk";

dayjs.extend(relativeTime);
const cx = classNames.bind(styles);

interface EstimateLayoutStep2Props {
    onStep2DataChange?: (data: Step2FormData) => void;
    initialData?: Step2FormData | null;
}

const EstimateLayoutStep2 = ({ onStep2DataChange, initialData }: EstimateLayoutStep2Props) => {
    const dispatch = useAppDispatch();
    const { estimateTemplates } = useAppSelector((state) => state.estimateSetting);

    const DEFAULT_TITLE_STEP = "Who we are...";
    const [formData, setFormData] = useState<Step2FormData | undefined>(() => ({
        ...initialData,
        titleStep2: initialData?.titleStep2 ?? DEFAULT_TITLE_STEP,
    }));

    const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const templateDropdownRef = useRef<HTMLDivElement>(null);
    const formDataRef = useRef(formData);

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target as Node)) {
                setTemplateDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleUpdateData = (update: Partial<Step2FormData>) => {
        const data: Step2FormData = { ...(formDataRef.current ?? {}), ...update } as Step2FormData;
        formDataRef.current = data;
        setFormData(data);
        onStep2DataChange?.(data);
    };

    const handleUseTemplateClick = () => {
        const nextOpen = !templateDropdownOpen;
        setTemplateDropdownOpen(nextOpen);
        if (nextOpen) {
            setTemplatesLoading(true);
            dispatch(handleGetEstimateTemplates()).finally(() => {
                setTemplatesLoading(false);
            });
        }
    };

    const handleSelectTemplate = (template: IEstimateTemplate) => {
        handleUpdateData({
            titleStep2: template.templateName ?? "",
            description: template.templateDescription ?? "",
        });
        setTemplateDropdownOpen(false);
    };

    return (
        <div className={cx('estimate-layout-step-2-container')}>
            <div className={cx('header')}>
                <TitleEditor value={formData?.titleStep2}
                    name={""}
                    placeholder={""}
                    inputkey={""}
                    isViewMode={false}
                    onChange={(value) => {
                        handleUpdateData({ titleStep2: value });
                    }}
                    styleView={{
                        fontSize: "20px",
                        fontWeight: 700,
                    }}
                />

                <div className={cx('button-container')} ref={templateDropdownRef}>
                    <div className={cx("template-dropdown-wrapper")}>
                        <button
                            type="button"
                            className={cx("left-content-body-menu-item")}
                            onClick={handleUseTemplateClick}
                        >
                            Use Template
                        </button>
                        {templateDropdownOpen && (
                            <div className={cx("template-dropdown")}>
                                {templatesLoading ? (
                                    <div className={cx("template-dropdown-loading")}>
                                        Loading...
                                    </div>
                                ) : (
                                    <ul className={cx("template-dropdown-list")}>
                                        {estimateTemplates.length === 0 ? (
                                            <li className={cx("template-dropdown-empty")}>No templates</li>
                                        ) : (
                                            estimateTemplates.map((template) => (
                                                <li key={template.id}>
                                                    <button
                                                        type="button"
                                                        className={cx("template-dropdown-item")}
                                                        onClick={() => handleSelectTemplate(template)}
                                                    >
                                                        {template.templateName || "Unnamed template"}
                                                    </button>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>

                    <PrimaryButton
                        title="View Page"
                        onClick={() => { }}
                        className={cx('btn-preview')}
                    />
                </div>
            </div>
            <div className={cx('content')}>
                <FormInpuEditor
                    name={"companyDescription"}
                    text={""}
                    placeholder={""}
                    inputkey={"companyDescription"}
                    value={formData?.description}
                    isViewMode={false}
                    errorMessage={""}
                    height="100%"
                    onChange={(value) => {
                        handleUpdateData({ description: value });
                    }}
                />
            </div>
        </div>
    );
};

export default EstimateLayoutStep2;